import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Grid, Stack, Typography, IconButton, Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const OnboardingLayout = ({
    step = 1,
    totalSteps = 3,
    title,
    subtitle,
    children,
    backLink = '/login',
    imageSrc,
    hideStepper = false,
    hideBackButton = false
}) => {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 20);
        return () => clearTimeout(t);
    }, []);

    return (
        <Grid container sx={{ minHeight: '100vh' }}>
            {/* LEFT SIDE */}
            <Grid
                item
                xs={12}
                md={6}
                sx={{
                    height: '100vh',
                    overflowY: 'auto',
                    py: 4,
                    px: { xs: 2, md: 6 },
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '10px'
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: theme.palette.primary.dark
                    }
                }}
            >
                {/* Top Row: Back + Step Indicator */}
                <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Grid item>
                        {!hideBackButton && (
                            <IconButton
                                component={Link}
                                to={backLink}
                                aria-label="Back"
                                size="small"
                                sx={{
                                    borderRadius: 1,
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <ArrowBack fontSize="small" />
                            </IconButton>
                        )}
                    </Grid>

                    <Grid item>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {!hideStepper && (
                                <>
                                    <Typography variant="caption" color="text.secondary">
                                        Step {step} of {totalSteps}
                                    </Typography>

                                    <Stack direction="row" spacing={0.5}>
                                        {Array.from({ length: totalSteps }).map((_, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: index + 1 === step ? theme.palette.primary.main : 'divider',
                                                    opacity: index + 1 === step ? 1 : 0.6
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                {/* Heading + subtitle */}
                <Box
                    sx={{
                        mb: 3,
                        transition: 'transform .35s ease, opacity .35s ease',
                        transform: mounted ? 'none' : 'translateY(10px)',
                        opacity: mounted ? 1 : 0
                    }}
                >
                    <Stack alignItems="flex-start" spacing={1}>
                        <Typography variant={matchDownSM ? 'h4' : 'h2'} gutterBottom>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body1" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* Children (form) */}
                {children}
            </Grid>

            {/* RIGHT SIDE IMAGE (desktop only) */}
            <Grid item xs={12} md={6} display={{ xs: 'none', md: 'block' }}>
                <Box
                    sx={{
                        height: '100vh',
                        position: 'sticky',
                        top: 0,
                        '& img': { height: '100%', width: '100%', objectFit: 'cover' }
                    }}
                >
                    {/* optional gradient overlay */}
                    {imageSrc && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(90deg, rgba(0,0,0,0.24), rgba(0,0,0,0.08))',
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                    {imageSrc && <img src={imageSrc} alt="onboarding" />}
                </Box>
            </Grid>
        </Grid>
    );
};

export default OnboardingLayout;
